import { HttpErrorResponse, HttpInterceptorFn } from "@angular/common/http";
import { inject } from "@angular/core";
import { catchError, throwError } from "rxjs";
import { AuthService } from "@core/services/auth.service";
import { ToastService } from "@shared/services/toast/toast.service";

/**
 * Endpoints d'authentification : un 401 y est un échec de connexion normal
 * (mauvais identifiants…), pas une session expirée. On ne déconnecte donc pas.
 */
const AUTH_ENDPOINTS = [
  "/auth/login",
  "/auth/register",
  "/auth/forgot-password",
  "/auth/reset-password",
];

const isAuthEndpoint = (url: string): boolean =>
  AUTH_ENDPOINTS.some((endpoint) => url.includes(endpoint));

/**
 * Déconnecte l'utilisateur et le renvoie sur la page de connexion dès que sa
 * session expire, plutôt que de le laisser subir des "accès refusé" partout.
 *
 * Deux déclencheurs :
 *  1. Proactif : token présent mais expiré (claim `exp`) → on n'appelle même pas
 *     le serveur, on déconnecte immédiatement.
 *  2. Réactif : réponse 401 sur un endpoint non-auth (token révoqué/expiré côté
 *     serveur) → on déconnecte.
 *
 * La notification n'est affichée qu'une fois : `logout()` supprime le token, donc
 * les requêtes concurrentes qui échouent ensuite voient `getToken()` null et sont
 * ignorées.
 */
export const sessionExpiredInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const toast = inject(ToastService);

  const expireSession = () => {
    // Déjà déconnecté par une requête concurrente : ne pas re-notifier / re-rediriger.
    if (!authService.getToken()) return;
    toast.warning("Votre session a expiré. Veuillez vous reconnecter.");
    authService.logout();
  };

  // 1. Vérification proactive avant l'envoi.
  if (
    !isAuthEndpoint(req.url) &&
    authService.getToken() &&
    authService.isTokenExpired()
  ) {
    expireSession();
    return throwError(
      () =>
        new HttpErrorResponse({
          status: 401,
          statusText: "Session expirée",
          url: req.url,
        })
    );
  }

  // 2. Vérification réactive sur la réponse.
  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 && !isAuthEndpoint(req.url)) {
        expireSession();
      }
      return throwError(() => error);
    })
  );
};
